import { getAuth0RuntimeConfig } from '../globalConfig/auth0RuntimeConfig';
import { getUiRuntimeIdentityConfig } from '../globalConfig/runtimeIdentityConfig';
import { getUiRuntimeConfig } from '../globalConfig/runtimeConfig';

/** @type {UiRuntimeConfigDependencies} */
const _p = {
    getAuth0RuntimeConfig,
    getUiRuntimeIdentityConfig,
    getUiRuntimeConfig,
    getGlobalObject: () => globalThis,
    getEnv: () => getDefaultEnv()
};
export const _private = _p;

/**
 * Returns the current public UI runtime config snapshot.
 *
 * The facade keeps provider setup inside `lib_ui-services` so UI consumers can
 * stop depending on `globalConfig()` without learning `@sstdev/lib_config`
 * manager/provider details.
 *
 * @param {object} [options]
 * @param {Record<string, string | undefined>} [options.env]
 * @param {Record<string, unknown>} [options.globalObject]
 * @param {'web' | 'native'} [options.platform]
 * @param {string[]} [options.sessionFeatureFlags]
 * @returns {UiRuntimeConfigSnapshot}
 */
export function getConfig(options = {}) {
    return _p.getUiRuntimeConfig({
        env: options.env ?? _p.getEnv(),
        globalObject: options.globalObject ?? _p.getGlobalObject(),
        platform: options.platform,
        sessionFeatureFlags: options.sessionFeatureFlags ?? []
    });
}

/**
 * Resolves public Auth0 runtime config without using `globalConfig().auth0`.
 *
 * Brand and environment are optional to support the existing default redbeam
 * production path, but callers with derived identity should pass them explicitly.
 *
 * @param {object} [options]
 * @param {string} [options.brand]
 * @param {string} [options.environment]
 * @param {Record<string, string | undefined>} [options.env]
 * @param {Record<string, unknown>} [options.globalObject]
 * @returns {UiAuth0RuntimeConfig}
 */
export function getAuth0Config(options = {}) {
    return _p.getAuth0RuntimeConfig({
        brand: options.brand,
        environment: options.environment,
        env: options.env ?? _p.getEnv(),
        globalObject: options.globalObject ?? _p.getGlobalObject()
    });
}

/**
 * Returns stable UI identity derived from the current runtime location.
 *
 * This covers values such as brand, environment, hostname, protocol, and
 * deployed/local status. Mutable session values like group number and tenant
 * settings intentionally remain on `globalConfig()`.
 *
 * @param {object} [options]
 * @param {boolean} [options.productionBuild]
 * @returns {UiRuntimeIdentityConfig}
 */
export function getIdentityConfig(options = {}) {
    return _p.getUiRuntimeIdentityConfig({
        productionBuild: options.productionBuild
    });
}

/**
 * Keeps feature flag reads on the runtime facade while `globalConfig()` still
 * owns the mutable session merge path through `setFeatureFlags`.
 *
 * @param {Parameters<typeof getConfig>[0]} [options]
 * @returns {string[]}
 */
export function getFeatureFlags(options) {
    return getConfig(options).featureFlags;
}

/** @type {Readonly<UiRuntimeConfigFacade>} */
const uiRuntimeConfig = Object.freeze({
    getAuth0Config,
    getConfig,
    getFeatureFlags,
    getIdentityConfig,
    getBrand: () => getIdentityConfig().brand,
    getEnvironment: () => getIdentityConfig().environment,
    getHost: () => getIdentityConfig().host,
    getHostname: () => getIdentityConfig().hostname,
    getIsDeployed: () => getIdentityConfig().isDeployed,
    getPort: () => getIdentityConfig().port,
    getProtocol: () => getIdentityConfig().protocol,
    getProtocolSocket: () => getIdentityConfig().protocolSocket,
    // Keep these getters explicit so TypeScript sees the facade's public API.
    // We could do something like `Object.fromEntries()` which technically works at runtime,
    // but it hides the generated names from JS/TS consumers that rely on JSDoc inference.
    getActionElementDebounceMilliseconds: () => getConfig().actionElementDebounceMilliseconds,
    getAuth0RefreshTokenAbsoluteLifetimeDays: () => getConfig().auth0RefreshTokenAbsoluteLifetimeDays,
    getBackgroundSyncBatchSize: () => getConfig().backgroundSyncBatchSize,
    getGetRecordsDebounceMilliseconds: () => getConfig().getRecordsDebounceMilliseconds,
    getGetRecordsThrottleMilliseconds: () => getConfig().getRecordsThrottleMilliseconds,
    getLoadingTimeout: () => getConfig().loadingTimeout,
    getMaxDeletePerTruncationPass: () => getConfig().maxDeletePerTruncationPass,
    getMaxRecordsInLimitedSizeCollection: () => getConfig().maxRecordsInLimitedSizeCollection,
    getMaxSecondsOfflineWithoutResync: () => getConfig().maxSecondsOfflineWithoutResync,
    getNetworkErrorRetryMilliseconds: () => getConfig().networkErrorRetryMilliseconds,
    getNotificationTimeout: () => getConfig().notificationTimeout,
    getNotificationTimeoutFormError: () => getConfig().notificationTimeoutFormError,
    getOfflineSessionExpirationDays: () => getConfig().offlineSessionExpirationDays,
    getOnlineSearchDebounceMilliseconds: () => getConfig().onlineSearchDebounceMilliseconds,
    getPageSizeForLimitSyncSizeRelations: () => getConfig().pageSizeForLimitSyncSizeRelations,
    getResyncRandomDelaySeconds: () => getConfig().resyncRandomDelaySeconds,
    getSearchDebounceMilliseconds: () => getConfig().searchDebounceMilliseconds,
    getSensorScanIntervalMilliseconds: () => getConfig().sensorScanIntervalMilliseconds
});

export default uiRuntimeConfig;

/** @returns {Record<string, string | undefined>} */
function getDefaultEnv() {
    const runtimeGlobal = /** @type {{ process?: { env?: Record<string, string | undefined> } }} */ (globalThis);
    return runtimeGlobal.process?.env ?? {};
}

/**
 * @typedef {object} UiRuntimeConfigOptions
 * @property {Record<string, string | undefined>} [env]
 * @property {Record<string, unknown>} [globalObject]
 * @property {'web' | 'native'} [platform]
 * @property {string[]} [sessionFeatureFlags]
 */

/**
 * @typedef {object} UiRuntimeConfigDependencies
 * @property {typeof getAuth0RuntimeConfig} getAuth0RuntimeConfig
 * @property {typeof getUiRuntimeIdentityConfig} getUiRuntimeIdentityConfig
 * @property {typeof getUiRuntimeConfig} getUiRuntimeConfig
 * @property {() => Record<string, unknown>} getGlobalObject
 * @property {() => Record<string, string | undefined>} getEnv
 */

/**
 * @typedef {object} UiAuth0RuntimeConfigOptions
 * @property {string} [brand]
 * @property {string} [environment]
 * @property {Record<string, string | undefined>} [env]
 * @property {Record<string, unknown>} [globalObject]
 */

/**
 * @typedef {object} UiAuth0RuntimeConfig
 * @property {string | undefined} [AUTH0_AUDIENCE]
 * @property {string | undefined} AUTH0_CLIENT_ID
 * @property {string} AUTH0_CONNECTION_NAME
 * @property {string | undefined} AUTH0_DOMAIN
 */

/**
 * @typedef {object} UiRuntimeIdentityConfig
 * @property {string} brand
 * @property {string} environment
 * @property {string} host
 * @property {string} hostname
 * @property {boolean} isDeployed
 * @property {string | number} port
 * @property {string} protocol
 * @property {string} protocolSocket
 */

/**
 * @typedef {object} UiRuntimeConfigSnapshot
 * @property {string[]} featureFlags
 * @property {number} actionElementDebounceMilliseconds
 * @property {number} auth0RefreshTokenAbsoluteLifetimeDays
 * @property {number} backgroundSyncBatchSize
 * @property {number} getRecordsDebounceMilliseconds
 * @property {number} getRecordsThrottleMilliseconds
 * @property {number} loadingTimeout
 * @property {number} maxDeletePerTruncationPass
 * @property {number} maxRecordsInLimitedSizeCollection
 * @property {number} maxSecondsOfflineWithoutResync
 * @property {number} networkErrorRetryMilliseconds
 * @property {number} notificationTimeout
 * @property {number} notificationTimeoutFormError
 * @property {number} offlineSessionExpirationDays
 * @property {number} onlineSearchDebounceMilliseconds
 * @property {number} pageSizeForLimitSyncSizeRelations
 * @property {number} resyncRandomDelaySeconds
 * @property {number} searchDebounceMilliseconds
 * @property {number} sensorScanIntervalMilliseconds
 */

/**
 * @typedef {object} UiRuntimeConfigFacade
 * @property {(options?: UiAuth0RuntimeConfigOptions) => UiAuth0RuntimeConfig} getAuth0Config
 * @property {(options?: UiRuntimeConfigOptions) => UiRuntimeConfigSnapshot} getConfig
 * @property {(options?: UiRuntimeConfigOptions) => string[]} getFeatureFlags
 * @property {(options?: { productionBuild?: boolean }) => UiRuntimeIdentityConfig} getIdentityConfig
 * @property {() => string} getBrand
 * @property {() => string} getEnvironment
 * @property {() => string} getHost
 * @property {() => string} getHostname
 * @property {() => boolean} getIsDeployed
 * @property {() => string | number} getPort
 * @property {() => string} getProtocol
 * @property {() => string} getProtocolSocket
 * @property {() => number} getActionElementDebounceMilliseconds
 * @property {() => number} getAuth0RefreshTokenAbsoluteLifetimeDays
 * @property {() => number} getBackgroundSyncBatchSize
 * @property {() => number} getGetRecordsDebounceMilliseconds
 * @property {() => number} getGetRecordsThrottleMilliseconds
 * @property {() => number} getLoadingTimeout
 * @property {() => number} getMaxDeletePerTruncationPass
 * @property {() => number} getMaxRecordsInLimitedSizeCollection
 * @property {() => number} getMaxSecondsOfflineWithoutResync
 * @property {() => number} getNetworkErrorRetryMilliseconds
 * @property {() => number} getNotificationTimeout
 * @property {() => number} getNotificationTimeoutFormError
 * @property {() => number} getOfflineSessionExpirationDays
 * @property {() => number} getOnlineSearchDebounceMilliseconds
 * @property {() => number} getPageSizeForLimitSyncSizeRelations
 * @property {() => number} getResyncRandomDelaySeconds
 * @property {() => number} getSearchDebounceMilliseconds
 * @property {() => number} getSensorScanIntervalMilliseconds
 */
